import React, { useState, useEffect } from "react";
import api from "../utils/api";
import { toast } from "react-toastify";

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [masterWallet, setMasterWallet] = useState(null);
  const [addressesWithFunds, setAddressesWithFunds] = useState([]);
  const [loadingFunds, setLoadingFunds] = useState(false);
  const [selectedAddresses, setSelectedAddresses] = useState([]);
  const [gasAmount, setGasAmount] = useState("0.002");
  const [sendGasFirst, setSendGasFirst] = useState(true);
  const [transferAddress, setTransferAddress] = useState("");
  const [transferUsdtAmount, setTransferUsdtAmount] = useState("");
  const [transferBnbAmount, setTransferBnbAmount] = useState("");
  const [loadingTransfer, setLoadingTransfer] = useState(false);
  const [withdrawals, setWithdrawals] = useState([]);
  const [withdrawalStats, setWithdrawalStats] = useState(null);
  const [loadingWithdrawals, setLoadingWithdrawals] = useState(false);
  const [processingWithdrawal, setProcessingWithdrawal] = useState(null);

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    try {
      const [statsResponse, usersResponse] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users?limit=10"),
      ]);
      setStats(statsResponse.data.stats);
      setUsers(usersResponse.data.users);
    } catch (error) {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const fetchMasterWallet = async () => {
    try {
      const response = await api.get("/admin/master-wallet");
      setMasterWallet(response.data.data);
    } catch (error) {
      toast.error("Failed to load master wallet");
    }
  };

  const fetchAddressesWithFunds = async () => {
    setLoadingFunds(true);
    try {
      const response = await api.get("/admin/addresses-with-funds");
      setAddressesWithFunds(response.data.data);
      toast.success(`Found ${response.data.count} addresses with funds`);
    } catch (error) {
      toast.error("Failed to scan addresses");
    } finally {
      setLoadingFunds(false);
    }
  };

  const handleConsolidateFunds = async () => {
    if (
      !window.confirm(
        "Are you sure you want to consolidate all funds? This will sweep USDT and remaining BNB from all addresses with funds to the master wallet.",
      )
    ) {
      return;
    }

    setLoadingFunds(true);
    try {
      const response = await api.post("/admin/consolidate-funds", {
        sendGasFirst,
        gasAmount,
      });
      toast.success(response.data.message);
      toast.info(
        `USDT: ${response.data.summary.totalUsdtSwept} | BNB: ${response.data.summary.totalBnbSwept} (${response.data.summary.bnbSweepCount} addresses)`,
      );

      // Refresh data
      await fetchMasterWallet();
      await fetchAddressesWithFunds();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to consolidate funds",
      );
    } finally {
      setLoadingFunds(false);
    }
  };

  const handleSendGas = async () => {
    if (selectedAddresses.length === 0) {
      toast.error("Please select at least one address");
      return;
    }

    if (
      !window.confirm(
        `Send ${gasAmount} BNB to ${selectedAddresses.length} address(es)?`,
      )
    ) {
      return;
    }

    setLoadingFunds(true);
    try {
      const response = await api.post("/admin/send-gas", {
        userIds: selectedAddresses,
        amount: gasAmount,
      });
      toast.success(response.data.message);
      setSelectedAddresses([]);

      // Refresh addresses
      await fetchAddressesWithFunds();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to send gas");
    } finally {
      setLoadingFunds(false);
    }
  };

  const toggleAddressSelection = (userId) => {
    setSelectedAddresses((prev) =>
      prev.includes(userId) ?
        prev.filter((id) => id !== userId)
      : [...prev, userId],
    );
  };

  const handleTransferUSDT = async () => {
    if (!transferAddress || !transferUsdtAmount) {
      toast.error("Please provide address and amount");
      return;
    }

    if (
      !window.confirm(
        `Transfer ${transferUsdtAmount} USDT to ${transferAddress}?`,
      )
    ) {
      return;
    }

    setLoadingTransfer(true);
    try {
      const response = await api.post("/admin/transfer-usdt", {
        toAddress: transferAddress,
        amount: transferUsdtAmount,
      });
      toast.success(response.data.message);
      toast.info(`TX: ${response.data.data.txHash}`);
      setTransferUsdtAmount("");
      setTransferAddress("");
      await fetchMasterWallet();
    } catch (error) {
      toast.error(error.response?.data?.message || "Transfer failed");
    } finally {
      setLoadingTransfer(false);
    }
  };

  const handleTransferBNB = async () => {
    if (!transferAddress || !transferBnbAmount) {
      toast.error("Please provide address and amount");
      return;
    }

    if (
      !window.confirm(
        `Transfer ${transferBnbAmount} BNB to ${transferAddress}?`,
      )
    ) {
      return;
    }

    setLoadingTransfer(true);
    try {
      const response = await api.post("/admin/transfer-bnb", {
        toAddress: transferAddress,
        amount: transferBnbAmount,
      });
      toast.success(response.data.message);
      toast.info(`TX: ${response.data.data.txHash}`);
      setTransferBnbAmount("");
      setTransferAddress("");
      await fetchMasterWallet();
    } catch (error) {
      toast.error(error.response?.data?.message || "Transfer failed");
    } finally {
      setLoadingTransfer(false);
    }
  };

  useEffect(() => {
    if (activeTab === "funds") {
      fetchMasterWallet();
      fetchAddressesWithFunds();
    } else if (activeTab === "withdrawals") {
      fetchWithdrawals();
      fetchWithdrawalStats();
    }
  }, [activeTab]);

  const fetchWithdrawals = async () => {
    setLoadingWithdrawals(true);
    try {
      // Fetch withdrawals that need admin attention (awaiting_confirmation, processing, pending)
      const response = await api.get("/withdrawals/all");
      // Filter for statuses that need admin action
      const pendingWithdrawals = response.data.withdrawals.filter(
        (w) =>
          w.status === "awaiting_confirmation" ||
          w.status === "processing" ||
          w.status === "pending",
      );
      setWithdrawals(pendingWithdrawals);
    } catch (error) {
      toast.error("Failed to load withdrawals");
    } finally {
      setLoadingWithdrawals(false);
    }
  };

  const fetchWithdrawalStats = async () => {
    try {
      const response = await api.get("/withdrawals/stats");
      setWithdrawalStats(response.data.data);
    } catch (error) {
      console.error("Failed to load withdrawal stats");
    }
  };

  const handleProcessWithdrawal = async (withdrawalId, action) => {
    const actionText = action === "approve" ? "approve and pay" : "reject";
    if (
      !window.confirm(
        `Are you sure you want to ${actionText} this withdrawal request?${action === "approve" ? " USDT will be sent from the master wallet." : ""}`,
      )
    ) {
      return;
    }

    setProcessingWithdrawal(withdrawalId);
    try {
      const response = await api.put(`/withdrawals/process/${withdrawalId}`, {
        action,
      });
      toast.success(response.data.message);
      if (action === "approve" && response.data.txHash) {
        toast.info(`Transaction: ${response.data.txHash.substring(0, 20)}...`);
      }
      // Refresh withdrawals and stats
      await fetchWithdrawals();
      await fetchWithdrawalStats();
    } catch (error) {
      toast.error(
        error.response?.data?.message || `Failed to ${action} withdrawal`,
      );
    } finally {
      setProcessingWithdrawal(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">Admin Dashboard</h1>
        <p className="text-slate-300 mt-1">System overview and management</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <p className="text-blue-100 text-sm">Total Users</p>
          <p className="text-3xl font-bold">{stats?.users.total || 0}</p>
        </div>
        <div className="card bg-gradient-to-br from-green-500 to-green-600 text-white">
          <p className="text-green-100 text-sm">Activated Users</p>
          <p className="text-3xl font-bold">{stats?.users.activated || 0}</p>
        </div>
        <div className="card bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <p className="text-purple-100 text-sm">Total Transactions</p>
          <p className="text-3xl font-bold">{stats?.transactions.total || 0}</p>
        </div>
        <div className="card bg-gradient-to-br from-orange-500 to-orange-600 text-white">
          <p className="text-orange-100 text-sm">Total Distributed</p>
          <p className="text-3xl font-bold">
            ${stats?.earnings.totalDistributed?.toFixed(2) || "0.00"}
          </p>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="card">
          <h3 className="font-bold text-white mb-2">Last 24 Hours</h3>
          <p className="text-3xl font-bold text-cyan-300">
            {stats?.users.newUsers.last24h || 0}
          </p>
          <p className="text-sm text-slate-400">New registrations</p>
        </div>
        <div className="card">
          <h3 className="font-bold text-white mb-2">Last 7 Days</h3>
          <p className="text-3xl font-bold text-cyan-300">
            {stats?.users.newUsers.last7d || 0}
          </p>
          <p className="text-sm text-slate-400">New registrations</p>
        </div>
        <div className="card">
          <h3 className="font-bold text-white mb-2">Last 30 Days</h3>
          <p className="text-3xl font-bold text-emerald-300">
            {stats?.users.newUsers.last30d || 0}
          </p>
          <p className="text-sm text-slate-400">New registrations</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-white/10">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab("overview")}
            className={`pb-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "overview" ?
                "border-cyan-400 text-cyan-200"
              : "border-transparent text-slate-400 hover:text-slate-200"
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab("users")}
            className={`pb-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "users" ?
                "border-cyan-400 text-cyan-200"
              : "border-transparent text-slate-400 hover:text-slate-200"
            }`}
          >
            Recent Users
          </button>
          <button
            onClick={() => setActiveTab("earnings")}
            className={`pb-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "earnings" ?
                "border-cyan-400 text-cyan-200"
              : "border-transparent text-slate-400 hover:text-slate-200"
            }`}
          >
            Top Earners
          </button>
          <button
            onClick={() => setActiveTab("funds")}
            className={`pb-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "funds" ?
                "border-cyan-400 text-cyan-200"
              : "border-transparent text-slate-400 hover:text-slate-200"
            }`}
          >
            Fund Management
          </button>
          <button
            onClick={() => setActiveTab("withdrawals")}
            className={`pb-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "withdrawals" ?
                "border-cyan-400 text-cyan-200"
              : "border-transparent text-slate-400 hover:text-slate-200"
            }`}
          >
            Withdrawals
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card">
            <h3 className="text-xl font-bold text-white mb-4">
              Transaction Breakdown
            </h3>
            <div className="space-y-3">
              {stats?.transactions.byType?.map((item) => (
                <div
                  key={item._id}
                  className="flex justify-between items-center p-3 bg-white/5 rounded-lg border border-white/10"
                >
                  <span className="text-slate-300 capitalize">
                    {item._id.replace(/_/g, " ")}
                  </span>
                  <div className="text-right">
                    <p className="font-bold text-white">{item.count}</p>
                    <p className="text-xs text-slate-400">
                      ${item.totalAmount?.toFixed(2)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="card">
            <h3 className="text-xl font-bold text-white mb-4">
              Stage Distribution
            </h3>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {stats?.earnings.byStage?.map((item) => (
                <div
                  key={item._id}
                  className="flex justify-between items-center p-2 bg-white/5 rounded border border-white/10"
                >
                  <span className="text-sm text-slate-300">
                    Stage {item._id}
                  </span>
                  <div className="text-right">
                    <p className="text-sm font-bold text-white">
                      {item.count} claims
                    </p>
                    <p className="text-xs text-slate-400">
                      ${item.totalPayout?.toFixed(2)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === "users" && (
        <div className="card">
          <h3 className="text-xl font-bold text-white mb-4">Recent Users</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-white/10">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                    User
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                    Stage
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                    Earnings
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                    Joined
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {users.map((user) => (
                  <tr key={user._id}>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm font-medium text-white">
                        {user.fullName}
                      </div>
                      <div className="text-xs text-slate-400">
                        @{user.username}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-300">
                      {user.email}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          user.isActivated ?
                            "bg-emerald-500/20 text-emerald-200"
                          : "bg-amber-500/20 text-amber-200"
                        }`}
                      >
                        {user.isActivated ? "Active" : "Pending"}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-white">
                      {user.currentStage}/26
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-white">
                      ${user.totalEarnings?.toFixed(2) || "0.00"}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-400">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === "earnings" && (
        <div className="card">
          <h3 className="text-xl font-bold text-white mb-4">Top Earners</h3>
          <div className="space-y-3">
            {stats?.topEarners?.map((user, index) => (
              <div
                key={user._id}
                className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/10"
              >
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 rounded-full bg-cyan-500/30 text-cyan-100 flex items-center justify-center font-bold border border-cyan-400/30">
                    #{index + 1}
                  </div>
                  <div>
                    <p className="font-medium text-white">{user.fullName}</p>
                    <p className="text-sm text-slate-400">@{user.username}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xl font-bold text-emerald-300">
                    ${user.totalEarnings?.toFixed(2)}
                  </p>
                  <p className="text-xs text-slate-400">
                    Ref: ${user.referralEarnings?.toFixed(2)} | Stage: $
                    {user.stageEarnings?.toFixed(2)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === "funds" && (
        <div className="space-y-6">
          {/* Master Wallet */}
          <div className="card bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-400/30">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center">
              <span className="text-2xl mr-2">💎</span>
              Master Wallet
            </h3>
            {masterWallet ?
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                  <span className="text-slate-300">Address</span>
                  <span className="text-white font-mono text-sm">
                    {masterWallet.address.slice(0, 10)}...
                    {masterWallet.address.slice(-8)}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                  <span className="text-slate-300">Wallet Index</span>
                  <span className="text-cyan-300 font-bold">
                    {masterWallet.index || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                  <span className="text-slate-300">USDT Balance</span>
                  <span className="text-emerald-300 font-bold text-lg">
                    {parseFloat(masterWallet.usdtBalance).toFixed(2)} USDT
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                  <span className="text-slate-300">BNB Balance</span>
                  <span className="text-amber-300 font-bold">
                    {parseFloat(masterWallet.bnbBalance).toFixed(6)} BNB
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                  <span className="text-slate-300">Network</span>
                  <span className="text-white">{masterWallet.network}</span>
                </div>
                {masterWallet.note && (
                  <div className="p-3 bg-purple-500/10 border border-purple-400/20 rounded-lg">
                    <p className="text-xs text-purple-200">
                      ℹ️ {masterWallet.note}
                    </p>
                  </div>
                )}
              </div>
            : <div className="text-center text-slate-400 py-4">
                Loading master wallet...
              </div>
            }
          </div>

          {/* Consolidation Controls */}
          <div className="card">
            <h3 className="text-xl font-bold text-white mb-4">
              Consolidation Settings
            </h3>
            <div className="space-y-4">
              <div className="p-3 bg-cyan-500/10 border border-cyan-400/20 rounded-lg">
                <p className="text-sm text-cyan-200">
                  ⚡ Gas fees are sent from master wallet → user wallets → USDT
                  swept back → remaining BNB swept back to master wallet
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Gas Amount (BNB)
                </label>
                <input
                  type="text"
                  value={gasAmount}
                  onChange={(e) => setGasAmount(e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="0.002"
                />
                <p className="text-xs text-slate-400 mt-1">
                  Amount of BNB to send for gas fees (sent to addresses with{" "}
                  {"<"} 0.001 BNB)
                </p>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="sendGasFirst"
                  checked={sendGasFirst}
                  onChange={(e) => setSendGasFirst(e.target.checked)}
                  className="w-4 h-4 text-purple-500 bg-white/5 border-white/10 rounded focus:ring-purple-500"
                />
                <label
                  htmlFor="sendGasFirst"
                  className="ml-2 text-sm text-slate-300"
                >
                  Automatically send gas to addresses before consolidation
                  (Recommended ✓)
                </label>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={fetchAddressesWithFunds}
                  disabled={loadingFunds}
                  className="flex-1 py-3 px-6 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg font-semibold transition-all disabled:opacity-50"
                >
                  {loadingFunds ? "Scanning..." : "Scan Addresses"}
                </button>
                <button
                  onClick={handleConsolidateFunds}
                  disabled={loadingFunds || addressesWithFunds.length === 0}
                  className="flex-1 py-3 px-6 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg font-semibold transition-all disabled:opacity-50"
                >
                  {loadingFunds ? "Processing..." : "Consolidate All Funds"}
                </button>
              </div>
            </div>
          </div>

          {/* Transfer Funds */}
          <div className="card">
            <h3 className="text-xl font-bold text-white mb-4">
              Transfer Funds from Master Wallet
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Recipient Address
                </label>
                <input
                  type="text"
                  value={transferAddress}
                  onChange={(e) => setTransferAddress(e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="0x..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    USDT Amount
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={transferUsdtAmount}
                      onChange={(e) => setTransferUsdtAmount(e.target.value)}
                      className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="0.00"
                    />
                    <button
                      onClick={handleTransferUSDT}
                      disabled={
                        loadingTransfer ||
                        !transferAddress ||
                        !transferUsdtAmount
                      }
                      className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold transition-all disabled:opacity-50"
                    >
                      {loadingTransfer ? "Sending..." : "Send USDT"}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    BNB Amount
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={transferBnbAmount}
                      onChange={(e) => setTransferBnbAmount(e.target.value)}
                      className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                      placeholder="0.00"
                    />
                    <button
                      onClick={handleTransferBNB}
                      disabled={
                        loadingTransfer ||
                        !transferAddress ||
                        !transferBnbAmount
                      }
                      className="px-6 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-semibold transition-all disabled:opacity-50"
                    >
                      {loadingTransfer ? "Sending..." : "Send BNB"}
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-3 bg-amber-500/10 border border-amber-400/20 rounded-lg">
                <p className="text-xs text-amber-200">
                  ⚠️ Warning: Transfers are irreversible. Double-check the
                  recipient address before confirming.
                </p>
              </div>
            </div>
          </div>

          {/* Addresses with Funds */}
          {addressesWithFunds.length > 0 && (
            <div className="card">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-white">
                  Addresses with Funds ({addressesWithFunds.length})
                </h3>
                <button
                  onClick={handleSendGas}
                  disabled={selectedAddresses.length === 0 || loadingFunds}
                  className="py-2 px-4 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-semibold transition-all disabled:opacity-50 text-sm"
                >
                  Send Gas to Selected ({selectedAddresses.length})
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-white/10">
                  <thead>
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                        <input
                          type="checkbox"
                          checked={
                            selectedAddresses.length ===
                            addressesWithFunds.length
                          }
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedAddresses(
                                addressesWithFunds.map((a) => a.userId),
                              );
                            } else {
                              setSelectedAddresses([]);
                            }
                          }}
                          className="w-4 h-4 text-purple-500 bg-white/5 border-white/10 rounded"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                        User
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                        Address
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                        USDT
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                        BNB
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                        Gas
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    {addressesWithFunds.map((addr) => (
                      <tr
                        key={addr.userId}
                        className={`${
                          selectedAddresses.includes(addr.userId) ?
                            "bg-purple-500/10"
                          : ""
                        }`}
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedAddresses.includes(addr.userId)}
                            onChange={() => toggleAddressSelection(addr.userId)}
                            className="w-4 h-4 text-purple-500 bg-white/5 border-white/10 rounded"
                          />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-white">
                          {addr.username}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs font-mono text-slate-300">
                          {addr.address.slice(0, 8)}...{addr.address.slice(-6)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${
                              addr.isActivated ?
                                "bg-emerald-500/20 text-emerald-200"
                              : "bg-amber-500/20 text-amber-200"
                            }`}
                          >
                            {addr.isActivated ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-emerald-300">
                          {addr.usdtBalance}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-amber-300">
                          {addr.bnbBalance}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {addr.needsGas && (
                            <span className="px-2 py-1 text-xs rounded-full bg-red-500/20 text-red-200">
                              Needs Gas
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Withdrawals Tab */}
      {activeTab === "withdrawals" && (
        <div className="space-y-6">
          {/* Withdrawal Stats */}
          {withdrawalStats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="card bg-gradient-to-br from-amber-500/10 to-amber-500/5 border border-amber-400/20">
                <h4 className="text-sm text-amber-200 mb-2">Awaiting Email</h4>
                <p className="text-2xl font-bold text-white">
                  {withdrawalStats.awaiting_confirmation?.count || 0}
                </p>
                <p className="text-sm text-amber-300 mt-1">
                  $
                  {(
                    withdrawalStats.awaiting_confirmation?.totalAmount || 0
                  ).toFixed(2)}
                </p>
              </div>
              <div className="card bg-gradient-to-br from-cyan-500/10 to-cyan-500/5 border border-cyan-400/20">
                <h4 className="text-sm text-cyan-200 mb-2">Ready to Process</h4>
                <p className="text-2xl font-bold text-white">
                  {withdrawalStats.processing?.count || 0}
                </p>
                <p className="text-sm text-cyan-300 mt-1">
                  ${(withdrawalStats.processing?.totalAmount || 0).toFixed(2)}
                </p>
              </div>
              <div className="card bg-gradient-to-br from-slate-500/10 to-slate-500/5 border border-slate-400/20">
                <h4 className="text-sm text-slate-200 mb-2">Old Pending</h4>
                <p className="text-2xl font-bold text-white">
                  {withdrawalStats.pending?.count || 0}
                </p>
                <p className="text-sm text-slate-300 mt-1">
                  ${(withdrawalStats.pending?.totalAmount || 0).toFixed(2)}
                </p>
              </div>
              <div className="card bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border border-emerald-400/20">
                <h4 className="text-sm text-emerald-200 mb-2">Completed</h4>
                <p className="text-2xl font-bold text-white">
                  {withdrawalStats.completed?.count || 0}
                </p>
                <p className="text-sm text-emerald-300 mt-1">
                  ${(withdrawalStats.completed?.totalAmount || 0).toFixed(2)}
                </p>
              </div>
              <div className="card bg-gradient-to-br from-rose-500/10 to-rose-500/5 border border-rose-400/20">
                <h4 className="text-sm text-rose-200 mb-2">Rejected</h4>
                <p className="text-2xl font-bold text-white">
                  {withdrawalStats.rejected?.count || 0}
                </p>
                <p className="text-sm text-rose-300 mt-1">
                  ${(withdrawalStats.rejected?.totalAmount || 0).toFixed(2)}
                </p>
              </div>
            </div>
          )}

          {/* Pending Withdrawals */}
          <div className="card">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-white">
                Pending Withdrawal Requests
              </h3>
              <button
                onClick={fetchWithdrawals}
                className="px-4 py-2 bg-cyan-500/20 text-cyan-200 rounded-lg hover:bg-cyan-500/30 transition-colors"
                disabled={loadingWithdrawals}
              >
                {loadingWithdrawals ? "Loading..." : "Refresh"}
              </button>
            </div>

            {loadingWithdrawals ?
              <div className="text-center py-8 text-slate-400">Loading...</div>
            : withdrawals.length === 0 ?
              <div className="text-center py-8 text-slate-400">
                No pending withdrawals
              </div>
            : <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left p-3 text-slate-300">User</th>
                      <th className="text-left p-3 text-slate-300">Amount</th>
                      <th className="text-left p-3 text-slate-300">Fee</th>
                      <th className="text-left p-3 text-slate-300">
                        Net Amount
                      </th>
                      <th className="text-left p-3 text-slate-300">Address</th>
                      <th className="text-left p-3 text-slate-300">Date</th>
                      <th className="text-left p-3 text-slate-300">Status</th>
                      <th className="text-right p-3 text-slate-300">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {withdrawals.map((withdrawal) => (
                      <tr
                        key={withdrawal._id}
                        className="border-b border-white/5 hover:bg-white/5"
                      >
                        <td className="p-3">
                          <div>
                            <p className="text-white font-medium">
                              {withdrawal.user.username}
                            </p>
                            <p className="text-xs text-slate-400">
                              {withdrawal.user.email}
                            </p>
                          </div>
                        </td>
                        <td className="p-3 text-white font-medium">
                          ${withdrawal.amount.toFixed(2)}
                        </td>
                        <td className="p-3 text-slate-300">
                          ${withdrawal.fee.toFixed(2)}
                        </td>
                        <td className="p-3 text-emerald-300 font-medium">
                          ${withdrawal.netAmount.toFixed(2)}
                        </td>
                        <td className="p-3">
                          <code className="text-xs text-cyan-300 bg-cyan-500/10 px-2 py-1 rounded">
                            {withdrawal.withdrawalAddress.substring(0, 10)}...
                            {withdrawal.withdrawalAddress.substring(
                              withdrawal.withdrawalAddress.length - 8,
                            )}
                          </code>
                        </td>
                        <td className="p-3 text-slate-300 text-sm">
                          {new Date(withdrawal.createdAt).toLocaleDateString()}
                        </td>
                        <td className="p-3">
                          <span
                            className={`text-xs px-2 py-1 rounded-full font-medium ${
                              withdrawal.status === "processing" ?
                                "bg-cyan-500/20 text-cyan-300"
                              : withdrawal.status === "awaiting_confirmation" ?
                                "bg-amber-500/20 text-amber-300"
                              : "bg-slate-500/20 text-slate-300"
                            }`}
                          >
                            {withdrawal.status === "awaiting_confirmation" ?
                              "Awaiting Email"
                            : withdrawal.status === "processing" ?
                              "Ready to Process"
                            : withdrawal.status}
                          </span>
                        </td>
                        <td className="p-3">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() =>
                                handleProcessWithdrawal(
                                  withdrawal._id,
                                  "approve",
                                )
                              }
                              disabled={processingWithdrawal === withdrawal._id}
                              className="px-3 py-1 bg-emerald-500/20 text-emerald-200 rounded hover:bg-emerald-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                            >
                              {processingWithdrawal === withdrawal._id ?
                                "Processing..."
                              : "Approve & Pay"}
                            </button>
                            <button
                              onClick={() =>
                                handleProcessWithdrawal(
                                  withdrawal._id,
                                  "reject",
                                )
                              }
                              disabled={processingWithdrawal === withdrawal._id}
                              className="px-3 py-1 bg-rose-500/20 text-rose-200 rounded hover:bg-rose-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                            >
                              Reject
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            }
          </div>

          {/* Info Note */}
          <div className="card bg-blue-500/10 border border-blue-400/20">
            <div className="flex items-start gap-3">
              <div className="text-blue-300 text-xl">ℹ️</div>
              <div>
                <h4 className="text-blue-200 font-semibold mb-1">
                  Withdrawal Processing
                </h4>
                <ul className="text-sm text-blue-100 space-y-1">
                  <li>
                    • Approving a withdrawal will send USDT from the master
                    wallet to the user's specified address
                  </li>
                  <li>
                    • The transaction will be broadcasted to the BSC network
                    immediately
                  </li>
                  <li>
                    • Rejecting a withdrawal will refund the amount + fee back
                    to the user's wallet balance
                  </li>
                  <li>• All actions are logged in the transaction history</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
